import { createFileRoute, redirect } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Package,
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  Edit,
  Trash2,
  Barcode,
  AlertCircle,
  Loader2,
  RefreshCw,
  Receipt,
} from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useDraftInvoiceStore } from '@/stores/draftInvoiceStore';
import { useProducts, useDeleteProduct } from '@/hooks/queries/useProducts';
import { ProductForm } from '@/components/products/ProductForm';
import { toast } from '@/components/ui/toast/useToast';
import type { Product } from '@lebusiness/types';

export const Route = createFileRoute('/products/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: ProductsPage,
});

function ProductsPage() {
  const { company } = useAuthStore();
  const { data: products = [], isLoading, error, refetch } = useProducts(company?.id || '');
  const deleteProduct = useDeleteProduct();
  const { addItem } = useDraftInvoiceStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const categories = ['all', 'Électronique', 'Fournitures', 'Mobilier', 'Autres'];

  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.barcode && product.barcode.includes(searchQuery));

    const matchesCategory =
      selectedCategory === 'all' || product.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStockStatus = (quantity: number, minLevel?: number) => {
    if (quantity === 0) {
      return { label: 'نفذ / Rupture', color: 'bg-red-100 text-red-700' };
    }
    if (minLevel && quantity <= minLevel) {
      return { label: 'منخفض / Faible', color: 'bg-yellow-100 text-yellow-700' };
    }
    return { label: 'متوفر / En stock', color: 'bg-emerald-100 text-emerald-700' };
  };

  const stats = {
    total: products.length,
    inStock: products.filter((p) => p.stockQuantity > (p.minStockLevel || 0)).length,
    lowStock: products.filter(
      (p) => p.minStockLevel && p.stockQuantity > 0 && p.stockQuantity <= p.minStockLevel
    ).length,
    outOfStock: products.filter((p) => p.stockQuantity === 0).length,
  };

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    setFormOpen(true);
  };

  const handleNew = () => {
    setSelectedProduct(null);
    setFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (deleteConfirmId === id) {
      try {
        await deleteProduct.mutateAsync(id);
        toast.success(
          'تم الحذف بنجاح / Supprimé avec succès',
          'تم حذف المنتج / Le produit a été supprimé'
        );
        setDeleteConfirmId(null);
      } catch (error) {
        console.error('Failed to delete product:', error);
        toast.error(
          'خطأ / Erreur',
          'فشل حذف المنتج / Échec de la suppression'
        );
      }
    } else {
      setDeleteConfirmId(id);
      // Reset after 3 seconds
      setTimeout(() => setDeleteConfirmId(null), 3000);
    }
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedProduct(null);
  };

  const handleAddToCart = (product: Product) => {
    addItem({
      productId: product.id,
      productCode: product.code,
      productName: product.name,
      quantity: 1,
      unitPrice: product.sale_price || product.salePrice,
      vat: product.vat,
      unit: product.unit,
      availableStock: product.stock_quantity, // ← ADD THIS!
    });
    // toast.success(
    //   'تمت الإضافة / Ajouté',
    //   `${product.name} تمت إضافته إلى الفاتورة / ajouté à la facture brouillon`
    // );
  };

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              خطأ في تحميل البيانات / Erreur de chargement
            </h3>
            <p className="text-gray-600">
              {error instanceof Error ? error.message : 'Une erreur est survenue'}
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              المنتجات / Produits
            </h1>
            <p className="mt-2 text-gray-600">
              إدارة المنتجات والمخزون / Gérez vos produits et stock
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm">
              <Upload className="w-4 h-4 mr-2" />
              استيراد / Importer
            </Button>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              تصدير / Exporter
            </Button>
            <Button
              size="sm"
              onClick={handleNew}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              منتج جديد / Nouveau
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">المجموع / Total</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">متوفر / En stock</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats.inStock}</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <Package className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">منخفض / Faible</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.lowStock}</p>
                </div>
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">نفذ / Rupture</p>
                  <p className="text-2xl font-bold text-red-600">{stats.outOfStock}</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <Package className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters & Search */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="بحث بالاسم، الرمز أو الباركود... / Rechercher par nom, code ou code-barres..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Category Filter */}
              <div className="flex gap-2">
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(category)}
                    className={
                      selectedCategory === category
                        ? 'bg-emerald-600 hover:bg-emerald-700'
                        : ''
                    }
                  >
                    {category === 'all' ? 'الكل / Tous' : category}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  className="ml-2"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Products Table */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b">
            <CardTitle>
              قائمة المنتجات / Liste des produits ({filteredProducts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        الرمز / Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        المنتج / Produit
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        الباركود / Code-barres
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        سعر الشراء / Prix achat
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        سعر البيع / Prix vente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        الكمية / Quantité
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        الحالة / Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        إجراءات / Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredProducts.map((product) => {
                      const stockStatus = getStockStatus(
                        product.stockQuantity,
                        product.minStockLevel
                      );
                      return (
                        <tr
                          key={product.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900">
                              {product.code}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {product.name}
                              </p>
                              <p className="text-xs text-gray-500">{product.category}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Barcode className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                {product.barcode || '-'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">
                              {formatCurrency(product.purchasePrice)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-semibold text-gray-900">
                              {formatCurrency(product.salePrice)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <span className="text-sm font-medium text-gray-900">
                                {product.stockQuantity} {product.unit}
                              </span>
                              {product.minStockLevel && (
                                <p className="text-xs text-gray-500">
                                  Min: {product.minStockLevel}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stockStatus.color}`}
                            >
                              {stockStatus.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleAddToCart(product)}
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                title="إضافة للفاتورة / Ajouter à la facture"
                              >
                                <Receipt className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(product)}
                              >
                                <Edit className="w-4 h-4 text-blue-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(product.id)}
                                disabled={deleteProduct.isPending}
                              >
                                {deleteConfirmId === product.id ? (
                                  <span className="text-xs text-red-600 font-medium">
                                    تأكيد؟
                                  </span>
                                ) : (
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && filteredProducts.length === 0 && (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  لا توجد منتجات / Aucun produit trouvé
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {products.length === 0
                    ? 'ابدأ بإضافة منتج جديد / Commencez par ajouter un nouveau produit'
                    : 'جرب تغيير معايير البحث / Essayez de modifier vos critères de recherche'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Product Form Modal */}
      <ProductForm
        open={formOpen}
        onOpenChange={handleFormClose}
        product={selectedProduct}
        onSuccess={() => {
          handleFormClose();
        }}
      />
    </DashboardLayout>
  );
}
